import type {
  CrossValidationField,
  CrossValidationFinding,
  CrossValidationSync,
  FinancialAssumptionsLike,
  FinancialPhase,
} from '../types/financialAssumptions';
import { finalPhaseCapacityMw } from '../types/financialAssumptions';

/**
 * FA16 — Cross-validation against Project Intent + SED.
 *
 * Surfaces inline warnings on the FA form when the captured financial
 * assumptions contradict signals already in the user's Project Intent or
 * the SED's industry-benchmark band for the technology. Each finding
 * optionally carries a `sync` payload describing a one-click resolution.
 *
 * Two patterns are codified here:
 *   1. CAPACITY mismatch — `FinancialAssumptions.capacityMW` (numeric) vs
 *      `ProjectIntent.powerRequirement` (discrete bucket). We map the
 *      bucket to a numeric range and check the FA value falls inside it.
 *   2. TECHNOLOGY contradiction — `FinancialAssumptions.technology = 'smr'`
 *      AND `ProjectIntent.nuclearInterest = 'no'` → contradiction.
 *
 * Plus tech-range outlier checks (CAPEX/kW, capacity factor) that don't
 * depend on Project Intent — they read tech-typical bands from a built-in
 * table and flag values >50% outside.
 *
 * Pure: no store reads/writes. The UI binding layer applies sync actions.
 */

/** Minimal `ProjectIntent` shape (mirror — keeps cross-validation
 *  independent of the full `siteRag.ts` type, which transitively imports a
 *  large graph). Only the fields we actually read. */
export interface ProjectIntentPhaseMinimal {
  id: string;
  sequence: number;
  capacityMW: number | null;
  targetInServiceDate?: string | null;
}

export interface ProjectIntentMinimal {
  powerRequirement?:
    | 'under-10mw'
    | '10-50mw'
    | '50-200mw'
    | '200-500mw'
    | '500-1000mw'
    | '1000-1500mw'
    | 'over-1500mw'
    | 'over-200mw' // legacy back-compat only
    | 'undecided';
  /** Optional exact MW (when the user has set it via the IP4 flow). */
  powerRequirementExactMW?: number | null;
  powerRequirementMW?: number | null; // SSOT-SENTINEL-OK — mirror of ProjectIntent.powerRequirementMW
  nuclearInterest?: 'primary' | 'considering' | 'no' | 'undecided';
  /** R-58 — per-phase intent for phased cross-checks. */
  phases?: ProjectIntentPhaseMinimal[];
}

interface BucketRange {
  min: number;
  max: number;
  label: string;
}

const POWER_BUCKETS: Record<NonNullable<ProjectIntentMinimal['powerRequirement']>, BucketRange | null> = {
  'under-10mw': { min: 0, max: 10, label: 'Under 10 MW' },
  '10-50mw': { min: 10, max: 50, label: '10–50 MW' },
  '50-200mw': { min: 50, max: 200, label: '50–200 MW' },
  '200-500mw': { min: 200, max: 500, label: '200–500 MW' },
  '500-1000mw': { min: 500, max: 1000, label: '500–1000 MW' },
  '1000-1500mw': { min: 1000, max: 1500, label: '1000–1500 MW' },
  'over-1500mw': { min: 1500, max: Number.POSITIVE_INFINITY, label: 'Over 1500 MW' },
  'over-200mw': { min: 200, max: Number.POSITIVE_INFINITY, label: '200 MW+ (legacy)' },
  undecided: null,
};

/** Tech-typical ranges. Sourced from NREL ATB 2024 + Lazard LCOE v17. */
interface TechRange {
  capexPerKwUsd: { min: number; max: number };
  capacityFactorPct: { min: number; max: number };
}

const TECH_RANGES: Record<string, TechRange> = {
  smr: {
    capexPerKwUsd: { min: 4500, max: 9000 },
    capacityFactorPct: { min: 85, max: 95 },
  },
  'gas-cc': {
    capexPerKwUsd: { min: 900, max: 1500 },
    capacityFactorPct: { min: 35, max: 75 },
  },
  'gas-sc': {
    capexPerKwUsd: { min: 600, max: 1200 },
    capacityFactorPct: { min: 5, max: 25 },
  },
  'solar-bess': {
    capexPerKwUsd: { min: 1500, max: 3000 },
    capacityFactorPct: { min: 18, max: 35 },
  },
  'wind-bess': {
    capexPerKwUsd: { min: 1300, max: 2400 },
    capacityFactorPct: { min: 28, max: 50 },
  },
};

const OUTLIER_TOLERANCE = 0.5; // ±50% outside band trips a finding.

// ─────────────────────────────────────────────────────────────────────────────
// Magnitude / plausibility caps
// ─────────────────────────────────────────────────────────────────────────────
//
// A single financial line item should never approach a trillion dollars. The
// largest real-world single-asset power projects (multi-unit nuclear) are on
// the order of $30-50B; $1e12 leaves >20x headroom over even the most extreme
// legitimate figure, so anything above it is almost certainly a units error or
// a fat-fingered entry (e.g. a $320-trillion working-capital figure).
const MAX_ABSOLUTE_USD = 1e12; // $1 trillion per single line item.

// A subordinate cashflow (working capital, decommissioning, contingency, land)
// that dwarfs total CAPEX by more than this multiple is implausible — these
// line items are normally fractions of CAPEX, not multiples of it.
const MAX_RATIO_TO_CAPEX = 10;

/** USD line items subjected to the absolute-magnitude cap. */
const ABSOLUTE_USD_FIELDS: ReadonlyArray<{ field: keyof FinancialAssumptionsLike; label: string }> = [
  { field: 'capexTotalUsd', label: 'Total CAPEX' },
  { field: 'workingCapitalUsd', label: 'Working capital' },
  { field: 'decommissioningUsd', label: 'Decommissioning' },
  { field: 'npvUsd', label: 'NPV' },
];

/**
 * USD line items checked against TOTAL CAPEX. These are subordinate to CAPEX in
 * any well-formed model, so a value many times larger than CAPEX is a red flag.
 * (CAPEX itself is excluded — it is the denominator.)
 */
const RATIO_TO_CAPEX_FIELDS: ReadonlyArray<{ field: keyof FinancialAssumptionsLike; label: string }> = [
  { field: 'workingCapitalUsd', label: 'Working capital' },
  { field: 'decommissioningUsd', label: 'Decommissioning' },
];

const ABSOLUTE_USD_FINDING_FIELD: CrossValidationField = 'capexPerKwUsd';

/** Compact human-readable USD label, e.g. `$320T`, `$4.5B`, `$900M`. */
function formatUsdMagnitude(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  return `${sign}$${Math.round(abs).toLocaleString('en-US')}`;
}

/**
 * Inputs for cross-validation. Each is optional — when null/undefined the
 * corresponding checks are skipped (no false-positive findings).
 */
export interface CrossValidationInput {
  financial: FinancialAssumptionsLike;
  /** R-58 native per-phase financial rows (when modelVersion >= 2). */
  financialPhases?: FinancialPhase[];
  projectIntent?: ProjectIntentMinimal | null;
  /** Optional override for the tech ranges (e.g. site-level SED band). */
  techRanges?: Record<string, TechRange>;
}

/**
 * Run all cross-validations. Returns a flat array of findings (severity-mixed).
 * UI: render in a warnings strip above the form.
 */
export function crossValidateFinancials(input: CrossValidationInput): CrossValidationFinding[] {
  const findings: CrossValidationFinding[] = [];
  const { financial, projectIntent, financialPhases } = input;
  const ranges = { ...TECH_RANGES, ...(input.techRanges ?? {}) };
  const phases = financialPhases ?? [];
  const phased = phases.length > 1 || (phases.length === 1 && phases[0].phaseId !== 'single-phase');
  // CUMULATIVE convention: campus capacity = FINAL/MAX cumulative phase
  // capacity, NOT the sum (summing a 50/100/200/300 build would give 650 and
  // ALWAYS warn against the true ultimate 300).
  const campusMwFromPhases = finalPhaseCapacityMw(phases);
  const effectiveCampusMw = campusMwFromPhases ?? financial.capacityMW ?? null;

  // ─────────────────────────────────────────────────────────────────────
  // R-58 — Per-phase vs intent + campus rollup checks
  // ─────────────────────────────────────────────────────────────────────
  if (phases.length > 0) {
    if (
      financial.capacityMW != null
      && campusMwFromPhases != null
      && Math.abs(financial.capacityMW - campusMwFromPhases) / Math.max(1, campusMwFromPhases) > 0.01
    ) {
      findings.push({
        id: 'cv-total-capacity-mismatch',
        field: 'capacityMW',
        severity: 'warning',
        message: `Campus capacity ${financial.capacityMW} MW does not match the final-phase cumulative capacity (${campusMwFromPhases} MW).`,
      });
    }

    const intentPhases = projectIntent?.phases ?? [];
    if (intentPhases.length > 0 && intentPhases.length !== phases.length) {
      findings.push({
        id: 'cv-phase-count-mismatch',
        field: 'phases',
        severity: 'warning',
        message: `Financial model has ${phases.length} phase(s) but Project Intent has ${intentPhases.length}.`,
      });
    }

    const intentById = new Map(intentPhases.map(p => [p.id, p]));
    for (const fp of phases) {
      const ip = intentById.get(fp.phaseId);
      if (!ip) continue;

      // CLUSTER PHASING — pin per-phase COD to the Project Intent SSOT. The
      // intent's `targetInServiceDate` is authoritative for the phasing
      // timeline; a financial phase whose in-service date disagrees should be
      // reconciled TO the intent (one-click sync), not the other way round.
      const intentCod = ip.targetInServiceDate ?? null;
      if (intentCod && fp.inServiceDate && fp.inServiceDate !== intentCod) {
        findings.push({
          id: 'cv-phase-cod-mismatch',
          field: 'phases',
          severity: 'warning',
          message: `${fp.label ?? fp.phaseId}: financial in-service ${fp.inServiceDate} vs Project Intent COD ${intentCod}. Pin the financial model to the Project Intent COD.`,
          sync: {
            targetSurface: 'financialAssumptions',
            field: `phases.${fp.phaseId}.inServiceDate`,
            newValue: intentCod,
            description: `Pin ${fp.label ?? fp.phaseId} in-service date to the Project Intent COD (${intentCod}).`,
          },
        });
      }

      if (
        fp.capacityMW != null
        && ip.capacityMW != null
        && Math.abs(fp.capacityMW - ip.capacityMW) / Math.max(1, ip.capacityMW) > 0.1
      ) {
        // CLUSTER PHASING — continuous-vs-installed BASIS reconciliation.
        // Project Intent's `capacityMW` is the INSTALLED (nameplate) capacity
        // and is the authoritative SSOT for the phasing table. A financial
        // phase whose capacity equals the intent installed MW DERATED by its
        // own capacity factor (continuous / average MW) is NOT a contradiction
        // — it is the same plant on a different basis (e.g. 300 MW installed ×
        // 82.5% LF ≈ 250 MW continuous). Detect that and downgrade to an
        // `info` basis clarification whose one-click sync PINS the financial
        // phase back to the intent INSTALLED basis (single, labelled basis).
        const cf =
          fp.capacityFactorPct != null && Number.isFinite(fp.capacityFactorPct)
            ? fp.capacityFactorPct
            : null;
        const isContinuousOfInstalled =
          cf != null
          && cf > 0
          && cf < 100
          && Math.abs(fp.capacityMW - ip.capacityMW * (cf / 100)) / Math.max(1, ip.capacityMW) <= 0.05;

        if (isContinuousOfInstalled) {
          findings.push({
            id: 'cv-phase-capacity-basis',
            field: 'phases',
            severity: 'info',
            message: `${fp.label ?? fp.phaseId}: financial ${fp.capacityMW} MW reads as CONTINUOUS (average) capacity — intent ${ip.capacityMW} MW is INSTALLED (nameplate) at ${cf}% capacity factor. Pin the phase to the installed basis so a single basis is used.`,
            sync: {
              targetSurface: 'financialAssumptions',
              field: `phases.${fp.phaseId}.capacityMW`,
              newValue: ip.capacityMW,
              description: `Pin ${fp.label ?? fp.phaseId} capacity to the Project Intent installed basis (${ip.capacityMW} MW).`,
            },
          });
        } else {
          findings.push({
            id: 'cv-phase-capacity-mismatch',
            field: 'phases',
            severity: 'warning',
            message: `${fp.label ?? fp.phaseId}: financial ${fp.capacityMW} MW vs intent ${ip.capacityMW} MW (installed). Pin the financial model to the Project Intent capacity.`,
            sync: {
              targetSurface: 'financialAssumptions',
              field: `phases.${fp.phaseId}.capacityMW`,
              newValue: ip.capacityMW,
              description: `Pin ${fp.label ?? fp.phaseId} capacity to the Project Intent installed basis (${ip.capacityMW} MW).`,
            },
          });
        }
      }
    }

    const sorted = [...phases].sort((a, b) => a.sequence - b.sequence);
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      const prevDate = prev.inServiceDate;
      const curDate = cur.inServiceDate;
      if (prevDate && curDate && curDate < prevDate) {
        findings.push({
          id: 'cv-phase-ordering-illogical',
          field: 'phases',
          severity: 'warning',
          message: `${cur.label ?? `Phase ${cur.sequence}`} in-service (${curDate}) is before ${prev.label ?? `Phase ${prev.sequence}`} (${prevDate}).`,
        });
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 1. Capacity mismatch — campus MW vs PI.powerRequirement (+ exact MW)
  // Phased sites: compare intent exact MW to Σ phase MW, not a single phase.
  // ─────────────────────────────────────────────────────────────────────
  if (effectiveCampusMw != null && projectIntent) {
    const exactMw = resolveExactMw(projectIntent);
    if (exactMw != null) {
      const diffPct = Math.abs(effectiveCampusMw - exactMw) / Math.max(1, exactMw);
      if (diffPct > 0.1) {
        findings.push(buildCapacityMismatchFinding(effectiveCampusMw, exactMw, projectIntent, phased));
      }
    }
    if (projectIntent.powerRequirement) {
      const range = POWER_BUCKETS[projectIntent.powerRequirement];
      if (range && (effectiveCampusMw < range.min || effectiveCampusMw > range.max)) {
        const exactMismatchFlagged =
          exactMw != null &&
          Math.abs(effectiveCampusMw - exactMw) / Math.max(1, exactMw) > 0.1;
        const exactHonored = exactMw != null && effectiveCampusMw === exactMw;
        if (!exactMismatchFlagged && !exactHonored) {
          const intentHasExact = exactMw != null;

          findings.push({
            id: 'cv-capacity-bucket-mismatch',
            field: 'capacityMW',
            severity: 'warning',
            message: `Capacity ${effectiveCampusMw} MW${phased ? ' (Σ phases)' : ''} falls outside Project Intent bucket "${range.label}". ${intentHasExact ? 'Intent has exact MW set — financial side should match.' : 'Sync or update one surface.'}`,
            sync: intentHasExact ? {
              targetSurface: 'financialAssumptions',
              field: 'capacityMW',
              newValue: exactMw!,
              description: `Update Financial Model capacity to ${exactMw} MW (matches exact intent)`,
            } : {
              targetSurface: 'projectIntent',
              field: 'powerRequirement',
              newValue: bucketFromMW(effectiveCampusMw),
              description: `Update Project Intent bucket to "${bucketLabel(effectiveCampusMw)}"`,
            },
          });
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 2. Technology contradiction — SMR + nuclearInterest=no
  // ─────────────────────────────────────────────────────────────────────
  if (financial.technology === 'smr' && projectIntent?.nuclearInterest === 'no') {
    findings.push({
      id: 'cv-tech-nuclear-rejected',
      field: 'technology',
      severity: 'error',
      message:
        'SMR selected but Project Intent says nuclear "rejected" — adjust either to match.',
      sync: {
        targetSurface: 'projectIntent',
        field: 'nuclearInterest',
        newValue: 'considering',
        description: 'Set Project Intent nuclearInterest to "considering"',
      },
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // 3. CAPEX/kW outside tech band (±50%)
  // ─────────────────────────────────────────────────────────────────────
  if (financial.technology && financial.capexPerKwUsd != null) {
    const r = ranges[financial.technology];
    if (r) {
      const loBound = r.capexPerKwUsd.min * (1 - OUTLIER_TOLERANCE);
      const hiBound = r.capexPerKwUsd.max * (1 + OUTLIER_TOLERANCE);
      if (financial.capexPerKwUsd < loBound || financial.capexPerKwUsd > hiBound) {
        findings.push({
          id: 'cv-capex-outside-range',
          field: 'capexPerKwUsd',
          severity: 'warning',
          message: `CAPEX $${financial.capexPerKwUsd.toLocaleString('en-US')}/kW is outside the ${financial.technology} industry band ($${r.capexPerKwUsd.min}-$${r.capexPerKwUsd.max}/kW) by more than 50%. Verify assumption.`,
        });
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 4. Capacity factor outside tech band (no tolerance — strict)
  // ─────────────────────────────────────────────────────────────────────
  if (financial.technology && financial.capacityFactorPct != null) {
    const r = ranges[financial.technology];
    if (r && (financial.capacityFactorPct < r.capacityFactorPct.min || financial.capacityFactorPct > r.capacityFactorPct.max)) {
      findings.push({
        id: 'cv-cf-outside-range',
        field: 'capacityFactorPct',
        severity: 'warning',
        message: `Capacity factor ${financial.capacityFactorPct}% is outside the ${financial.technology} typical range (${r.capacityFactorPct.min}-${r.capacityFactorPct.max}%). Verify.`,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // 5. Magnitude / plausibility caps — catch units errors & fat-finger
  //    entries (e.g. a $320-trillion working-capital figure) that would
  //    otherwise pass silently and corrupt every downstream calculation.
  // ─────────────────────────────────────────────────────────────────────
  for (const { field, label } of ABSOLUTE_USD_FIELDS) {
    const v = financial[field];
    if (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) > MAX_ABSOLUTE_USD) {
      findings.push({
        id: `cv-magnitude-${String(field)}`,
        field: ABSOLUTE_USD_FINDING_FIELD,
        severity: 'error',
        message: `${label} of ${formatUsdMagnitude(v)} exceeds the plausibility ceiling (${formatUsdMagnitude(MAX_ABSOLUTE_USD)}) — likely a units error or typo. Verify the value.`,
      });
    }
  }

  const capex = financial.capexTotalUsd;
  if (typeof capex === 'number' && Number.isFinite(capex) && capex > 0) {
    for (const { field, label } of RATIO_TO_CAPEX_FIELDS) {
      const v = financial[field];
      if (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) > capex * MAX_RATIO_TO_CAPEX) {
        findings.push({
          id: `cv-ratio-${String(field)}`,
          field: ABSOLUTE_USD_FINDING_FIELD,
          severity: 'warning',
          message: `${label} of ${formatUsdMagnitude(v)} is more than ${MAX_RATIO_TO_CAPEX}× total CAPEX (${formatUsdMagnitude(capex)}) — implausibly large relative to project cost. Verify the value.`,
        });
      }
    }
  }

  return findings;
}

function resolveExactMw(projectIntent: ProjectIntentMinimal): number | null {
  const raw = projectIntent.powerRequirementExactMW ?? projectIntent.powerRequirementMW;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  return null;
}

function buildCapacityMismatchFinding(
  faCapacity: number,
  piCapacity: number,
  projectIntent: ProjectIntentMinimal,
  phased: boolean,
): CrossValidationFinding {
  // Default sync direction: trust Project Intent (it's typically set
  // earlier in the flow), so propose updating the FA value. The UI can
  // expose the inverse via an "Override" action.
  const sync: CrossValidationSync = {
    targetSurface: 'financialAssumptions',
    field: 'capacityMW',
    newValue: piCapacity,
    description: phased
      ? `Set campus capacity rollup to ${piCapacity} MW (matches intent total)`
      : `Set FA capacity to ${piCapacity} MW (Project Intent value)`,
  };
  return {
    id: 'cv-capacity-mismatch',
    field: 'capacityMW',
    severity: 'warning',
    message: `Capacity ${faCapacity} MW${phased ? ' (Σ phases)' : ''} differs from Project Intent (${piCapacity} MW${
      projectIntent.powerRequirement ? `, bucket "${POWER_BUCKETS[projectIntent.powerRequirement]?.label ?? projectIntent.powerRequirement}"` : ''
    }) — which is correct?`,
    sync,
  };
}

/**
 * Inverse of POWER_BUCKETS: given a numeric MW, return the bucket key.
 * Used to populate the "Update Project Intent to X" sync action.
 */
function bucketFromMW(
  mw: number,
): NonNullable<ProjectIntentMinimal['powerRequirement']> {
  if (mw < 10) return 'under-10mw';
  if (mw < 50) return '10-50mw';
  if (mw <= 200) return '50-200mw';
  if (mw <= 500) return '200-500mw';
  if (mw <= 1000) return '500-1000mw';
  if (mw <= 1500) return '1000-1500mw';
  return 'over-1500mw';
}

function bucketLabel(mw: number): string {
  const key = bucketFromMW(mw);
  return (key && POWER_BUCKETS[key]?.label) ?? 'Undecided';
}

/**
 * Apply a `CrossValidationSync` action against the current state. Pure —
 * returns the next-state patches without mutating. The UI binding layer
 * dispatches these patches into the store actions.
 *
 * @returns `{ financialPatch?, projectIntentPatch? }` — at most one is set.
 */
export function applySync(
  sync: CrossValidationSync,
  current: { financial: FinancialAssumptionsLike; projectIntent?: ProjectIntentMinimal | null },
): {
  financialPatch?: Partial<FinancialAssumptionsLike>;
  projectIntentPatch?: Partial<ProjectIntentMinimal>;
} {
  if (sync.targetSurface === 'financialAssumptions') {
    return {
      financialPatch: {
        ...current.financial,
        [sync.field as keyof FinancialAssumptionsLike]: sync.newValue as never,
      },
    };
  }
  return {
    projectIntentPatch: {
      ...(current.projectIntent ?? {}),
      [sync.field as keyof ProjectIntentMinimal]: sync.newValue as never,
    },
  };
}

/**
 * Helper: filter findings by severity — useful for the form's red-strip
 * (`error`) vs amber-strip (`warning`) split.
 */
export function findingsBySeverity(
  findings: CrossValidationFinding[],
  severity: CrossValidationFinding['severity'],
): CrossValidationFinding[] {
  return findings.filter((f) => f.severity === severity);
}

export const __test__ = {
  POWER_BUCKETS,
  TECH_RANGES,
  OUTLIER_TOLERANCE,
  MAX_ABSOLUTE_USD,
  MAX_RATIO_TO_CAPEX,
  bucketFromMW,
  bucketLabel,
  formatUsdMagnitude,
};

export type { CrossValidationField };
